/* eslint-disable */
import { API_URL } from '../../config';
import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAppData } from '../../context/AppDataContext';
import { useBilling } from '../billing/BillingContext';
import { UpgradeModal } from '../billing/UpgradePrompts';
import { authAPI } from '../../services/api';

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const { loading, loadAllData, dataError, payments, calendarTasks, projects, properties, tenants } = useAppData();
  const { canAccess, billing } = useBilling();
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useAppData();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed]     = useState(false);
  const [upgradeModal, setUpgradeModal] = useState(null); // { feature }
  const [resendingVerification, setResendingVerification] = useState(false);
  const [verificationMsg, setVerificationMsg] = useState('');
  const [supportCopied, setSupportCopied] = useState(false);

  // BUG FIX / robustness: mailto: links only do anything if the browser/OS
  // has a default mail client configured — plenty of setups don't (webmail-
  // only, locked-down work machines, some Windows configs), and clicking
  // just silently does nothing, no error, no feedback. Given support needs
  // to actually be reachable for every user regardless of their mail setup,
  // this doesn't preventDefault (so mailto: still fires and works fine for
  // anyone it works for) — it additionally copies the address to the
  // clipboard and shows a clear on-screen confirmation, so there's always
  // something the user can see happened and can paste anywhere (webmail,
  // phone, notes app) even when the mailto: attempt goes nowhere.
  const handleSupportClick = async () => {
    try {
      await navigator.clipboard.writeText('info@casavello.io');
      setSupportCopied(true);
      setTimeout(() => setSupportCopied(false), 3000);
    } catch (e) { /* clipboard API unavailable or blocked — mailto: attempt is still the fallback */ }
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    setVerificationMsg('');
    try {
      await authAPI.resendVerification();
      setVerificationMsg('✅ Verification email sent — check your inbox.');
    } catch (err) { setVerificationMsg(err?.error || 'Failed to send.'); }
    finally { setResendingVerification(false); }
  };

  const hoursRemaining = (() => {
    if (!user?.verificationDeadline) return null;
    const diff = new Date(user.verificationDeadline).getTime() - Date.now();
    return diff > 0 ? Math.max(1, Math.ceil(diff / (1000 * 60 * 60))) : 0;
  })();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // --- Badge counts derived from live data ---
  const today = new Date();
  const todayStr = today.toISOString().substring(0, 10);

  const overduePaymentsCount = useMemo(() => {
    // Count distinct tenants with no payment in the last 35 days
    // Simple heuristic: payments where date is > 35 days ago and no newer payment for same room
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 35);
    const recentRooms = new Set(
      payments
        .filter(p => new Date(p.date) >= cutoff)
        .map(p => p.room_id || p.roomId)
        .filter(Boolean)
    );
    // Active rooms (occupied) that have no recent payment
    const activeRooms = properties.flatMap(prop =>
      (prop.rooms || []).filter(r => r.status === 'occupied' || r.tenant_name)
    );
    return activeRooms.filter(r => !recentRooms.has(r.id)).length;
  }, [payments, properties]);

  const overdueTasksCount = useMemo(() => {
    return calendarTasks.filter(t => {
      if (t.completed || t.status === 'completed') return false;
      const due = t.due_date || t.dueDate;
      return due && due.substring(0, 10) < todayStr;
    }).length;
  }, [calendarTasks, todayStr]);

  const pendingProjectsCount = useMemo(() => {
    return projects.filter(p => p.status === 'in_progress' || p.status === 'planning').length;
  }, [projects]);

  const expiringLeasesCount = useMemo(() => {
    if (!tenants) return 0;
    const soon = new Date();
    soon.setDate(soon.getDate() + 60);
    return tenants.filter(t => {
      if (t.status !== 'active') return false;
      if (!t.lease_end) return false;
      const end = new Date(t.lease_end.substring(0, 10) + 'T12:00:00');
      return end >= today && end <= soon;
    }).length;
  }, [tenants, today]);

  const orgLogo = settings?.org_logo || null;
  const orgName = settings?.company_name || user?.organizationName || 'casavello';

  const navItems = [
    { path: '/',                 label: 'Dashboard',             icon: 'ti-layout-dashboard', feature: null },
    { path: '/properties',       label: 'Properties',            icon: 'ti-building',          feature: null,       count: properties.length || null },
    { path: '/transactions',     label: 'Transactions',          icon: 'ti-receipt-2',         feature: null },
    { path: '/tenants',          label: 'Tenants',               icon: 'ti-users',             feature: null,       count: expiringLeasesCount || null, countDanger: true },
    { path: '/service-requests', label: 'Service Requests',      icon: 'ti-tool',              feature: 'service' },
    { path: '/contractors',      label: 'Contractors',           icon: 'ti-address-book',      feature: 'contractors' },
    // COMPANY-006: 'Company Expenses' removed. Transactions now shows company
    // expenses alongside property income and expenses, with a per-company
    // filter, so a separate tab showing a subset of the same data was a
    // second place to look for one answer. The /company route survives as a
    // redirect (see App.js) so bookmarks and any in-app links still land
    // somewhere sensible.
    { path: '/projects',         label: 'Projects',              icon: 'ti-tools',             feature: 'projects', count: pendingProjectsCount || null },
    { path: '/calendar',         label: 'Calendar',              icon: 'ti-calendar',          feature: 'calendar', count: overdueTasksCount || null, countDanger: true },
    { path: '/prospects',        label: 'Prospective Properties',icon: 'ti-home-search',       feature: 'prospects' },
    { path: '/reports',          label: 'Reports',               icon: 'ti-chart-bar',         feature: null },
  ];

  const handleNavClick = (item) => {
    if (!item.feature || canAccess(item.feature)) {
      navigate(item.path); setSidebarOpen(false);
    } else {
      setUpgradeModal({ feature: item.feature });
    }
  };

  return (
    <div className="app-shell">
      {/* ── Mobile overlay ── */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ── Sidebar ── */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar--open' : ''} ${collapsed ? 'sidebar--collapsed' : ''}`}>
        {/* Org identity */}
        <div className="sidebar-brand" onClick={() => { navigate('/'); setSidebarOpen(false); }}>
          <div className="sidebar-logo">
            {orgLogo
              ? <img src={orgLogo} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '0.375rem' }} />
              : <img src="/casavello-icon-500.png" alt="casavello" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '0.375rem' }} />
            }
          </div>
          <div className="sidebar-brand-text">
            <span className="sidebar-brand-name">{orgName}</span>
            <span className="sidebar-brand-sub">
              {user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Member'}
            </span>
          </div>
        </div>

        <div className="sidebar-divider" />

        {/* Main nav */}
        <nav className="sidebar-nav" aria-label="Main navigation">
          {navItems.map(item => {
            const locked = item.feature && !canAccess(item.feature);
            return (
              <button
                key={item.path}
                className={`sidebar-nav-item ${isActive(item.path) && !locked ? 'sidebar-nav-item--active' : ''}`}
                onClick={() => handleNavClick(item)}
                data-tooltip={locked ? `Upgrade to unlock ${item.label}` : item.label}
                title={locked ? `Upgrade to unlock ${item.label}` : item.label}
                style={locked ? { opacity: 0.45, cursor: 'pointer' } : {}}
              >
                <i className={`ti ${locked ? 'ti-lock' : item.icon}`} aria-hidden="true" />
                <span className="sidebar-nav-label">{item.label}</span>
                {!locked && item.count > 0 && (
                  <span className={`sidebar-badge ${item.countDanger ? 'sidebar-badge--danger' : 'sidebar-badge--neutral'}`}>
                    {item.count}
                  </span>
                )}
                {locked && <span className="sidebar-badge sidebar-badge--neutral" style={{ fontSize: '0.6rem' }}>PRO</span>}
              </button>
            );
          })}

          {isAdmin() && (
            <button
              className={`sidebar-nav-item ${isActive('/admin') ? 'sidebar-nav-item--active' : ''}`}
              onClick={() => { navigate('/admin'); setSidebarOpen(false); }}
              data-tooltip="Administration"
              title="Administration"
            >
              <i className="ti ti-activity" aria-hidden="true" />
              <span className="sidebar-nav-label">Administration</span>
            </button>
          )}
        </nav>

        <div className="sidebar-spacer" />
        <div className="sidebar-divider" />

        {/* Bottom actions */}
        <div className="sidebar-footer">
          {/* Support: a real, always-visible touchpoint. info@casavello.io
              previously only existed inside the Privacy Policy/ToS/EULA
              text — a monitored inbox nobody can find is barely better than
              no support at all. Placed here, not gated by role or plan,
              because everyone needs a way to reach support regardless of
              what they're on. A plain mailto: link rather than an in-app
              contact form — simplest thing that actually works today. Uses
              sidebar-nav-item/sidebar-nav-label, same classes as the
              buttons below, so it collapses to icon-only correctly for
              free — no separate CSS rule needed for this one. */}
          <a
            href="mailto:info@casavello.io"
            className="sidebar-nav-item"
            onClick={handleSupportClick}
            data-tooltip={supportCopied ? 'Copied!' : 'Help & Support'}
            title="Help & Support — info@casavello.io"
          >
            <i className={`ti ${supportCopied ? 'ti-check' : 'ti-life-buoy'}`} aria-hidden="true" />
            <span className="sidebar-nav-label">{supportCopied ? 'Copied email!' : 'Help & Support'}</span>
          </a>

          <button
            className="sidebar-nav-item"
            onClick={() => { navigate('/settings'); setSidebarOpen(false); }}
            data-tooltip="Settings"
            title="Settings"
          >
            <i className="ti ti-settings" aria-hidden="true" />
            <span className="sidebar-nav-label">Settings</span>
          </button>

          <button
            className="sidebar-nav-item sidebar-nav-item--refresh"
            onClick={loadAllData}
            disabled={loading}
            data-tooltip="Refresh data"
            title="Refresh all data"
          >
            <i className={`ti ti-refresh ${loading ? 'spin' : ''}`} aria-hidden="true" />
            <span className="sidebar-nav-label">{loading ? 'Refreshing…' : 'Refresh data'}</span>
          </button>

          {/* User row */}
          <div className="sidebar-user">
            <div className="sidebar-avatar" aria-hidden="true">
              {(user?.username || 'U').charAt(0).toUpperCase()}
            </div>
            <span className="sidebar-user-name">{user?.username}</span>
            <button className="sidebar-logout" onClick={handleLogout} title="Sign out">
              <i className="ti ti-logout" aria-hidden="true" />
            </button>
          </div>

          {/* Collapse toggle — desktop only */}
          <button
            className="sidebar-toggle"
            onClick={() => setCollapsed(c => !c)}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <i className={`ti ${collapsed ? 'ti-chevrons-right' : 'ti-chevrons-left'}`} aria-hidden="true" />
            <span className="sidebar-toggle-label">Collapse</span>
          </button>
        </div>
      </aside>

      {/* ── Main area ── */}
      <div className="app-main">
        {/* Mobile top bar */}
        <header className="mobile-topbar">
          <button className="mobile-menu-btn" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
            <i className="ti ti-menu-2" aria-hidden="true" />
          </button>
          <span className="mobile-topbar-title">
            {orgLogo
              ? <img src={orgLogo} alt="" style={{ height: '24px', objectFit: 'contain' }} />
              : orgName
            }
          </span>
          <button className="mobile-menu-btn" onClick={handleLogout} aria-label="Sign out">
            <i className="ti ti-logout" aria-hidden="true" />
          </button>
        </header>

        {/* VERIFY-003: this banner already existed and already handled the
            "expired" copy correctly — but it stayed the same soft amber
            reminder whether the deadline was 40 hours away or had already
            passed. Now that expiry has a real consequence (writes are
            restricted server-side, see authenticateToken), the banner
            escalates to match: same component, stronger colour and explicit
            wording once it's no longer just a reminder. */}
        {user && !user.emailVerified && (
          <div style={{ background: hoursRemaining === 0 ? '#fef2f2' : '#fef3c7', borderBottom: `1px solid ${hoursRemaining === 0 ? '#fca5a5' : '#fde047'}`, color: hoursRemaining === 0 ? '#991b1b' : '#92400e', padding: '0.5rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.8125rem', gap: '1rem', flexWrap: 'wrap' }}>
            <span>
              {hoursRemaining === 0 ? (
                <>⚠️ <strong>Your email verification has expired.</strong> You can still view everything, but saving or changing anything is paused until you verify.</>
              ) : (
                <>📧 Please verify your email address{hoursRemaining !== null && ` — ${hoursRemaining} hour${hoursRemaining === 1 ? '' : 's'} remaining`}.</>
              )}
              {verificationMsg && <strong style={{ marginLeft: '0.75rem' }}>{verificationMsg}</strong>}
            </span>
            <button onClick={handleResendVerification} disabled={resendingVerification} style={{ background: hoursRemaining === 0 ? '#991b1b' : '#92400e', color: '#fff', border: 'none', borderRadius: '0.375rem', padding: '0.375rem 0.875rem', fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {resendingVerification ? 'Sending…' : 'Resend verification email'}
            </button>
          </div>
        )}

        {/* Free plan banner */}
        {billing?.plan === 'free' && (
          <div style={{ background: 'linear-gradient(90deg,#1e3a5f 0%,#166534 100%)', color: '#fff', padding: '0.5rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.8125rem', gap: '1rem', flexWrap: 'wrap' }}>
            <span>✨ You're on the <strong>Free plan</strong> — {billing.propertyCount || 0} of {billing.propertyLimit} properties used. Upgrade to unlock reports, projects, calendar and more.</span>
            <button onClick={() => setUpgradeModal({ feature: 'reports' })} style={{ background: '#fff', color: '#166534', border: 'none', borderRadius: '0.375rem', padding: '0.375rem 0.875rem', fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
              View Plans →
            </button>
          </div>
        )}

        {/* Past due warning */}
        {billing?.planStatus === 'past_due' && (
          <div style={{ background: '#fef2f2', borderBottom: '1px solid #fecaca', color: '#dc2626', padding: '0.5rem 1.5rem', fontSize: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>⚠️ Your last payment failed. Please update your billing details to keep your plan active.</span>
            <button onClick={async () => {
              try {
                const BASE = API_URL;
                const token = localStorage.getItem('token');
                const res = await fetch(`${BASE}/api/billing/create-portal-session`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
                const data = await res.json();
                if (data.url) window.location.href = data.url;
              } catch {}
            }} style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: '0.375rem', padding: '0.375rem 0.875rem', fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer' }}>
              Update Payment →
            </button>
          </div>
        )}

        {/* Data error banner */}
        {dataError && (
          <div className="data-error-banner">
            <i className="ti ti-alert-triangle" aria-hidden="true" />
            Data failed to load: {dataError} —{' '}
            <button onClick={loadAllData}>Retry</button>
          </div>
        )}

        <main className="main-content">
          {children}
        </main>

        {/* Footer — shown on every screen */}
        <footer style={{ padding: '1.5rem', textAlign: 'center', borderTop: '1px solid var(--border)', marginTop: 'auto' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>casavello by Hibernian Technologies Limited</div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1.25rem', fontSize: '0.75rem' }}>
            <a href="/privacy" style={{ color: 'var(--text-secondary)', textDecoration: 'underline' }}>Privacy Policy</a>
            <a href="/terms" style={{ color: 'var(--text-secondary)', textDecoration: 'underline' }}>Terms of Service</a>
            <a href="/eula" style={{ color: 'var(--text-secondary)', textDecoration: 'underline' }}>EULA</a>
          </div>
        </footer>
      </div>

      {/* Upgrade modal — rendered at layout level */}
      {upgradeModal && (
        <UpgradeModal feature={upgradeModal.feature} onClose={() => setUpgradeModal(null)} />
      )}
    </div>
  );
}
